import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { brand } from '@app/shared/brand';

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  focusTitle = true,
  docTitle,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  /** Screens with their own primary input (search) opt out of title focus. */
  focusTitle?: boolean;
  /** Browser tab title when `title` isn't plain text. */
  docTitle?: string;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  const tabTitle = typeof title === 'string' ? title : docTitle;
  useEffect(() => {
    document.title = tabTitle ? `${tabTitle} · ${brand.productName}` : brand.productName;
  }, [tabTitle]);
  // Move focus to the page title after navigation so screen readers announce the new page.
  useEffect(() => {
    if (focusTitle) ref.current?.focus({ preventScroll: true });
  }, [focusTitle]);
  return (
    <header className="ui-page-header">
      <div className="ui-page-header__titles">
        {back && (
          <Link to={back.to} className="ui-back">
            <ChevronLeft aria-hidden="true" />
            {back.label}
          </Link>
        )}
        <h1 ref={ref} tabIndex={-1} style={{ outline: 'none' }}>
          {title}
        </h1>
        {subtitle && <div className="ui-page-header__subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="ui-page-header__actions">{actions}</div>}
    </header>
  );
}

export function Section({ title, icon, action, children, id }: { title: ReactNode; icon?: ReactNode; action?: ReactNode; children: ReactNode; id?: string }) {
  // Every section is named by its heading, so assistive tech can list and jump between them.
  const generated = useId();
  const titleId = `${id ?? generated}-title`;
  return (
    <section className="ui-section" aria-labelledby={titleId}>
      <div className="ui-section__head">
        <h2 className="ui-section__title" id={titleId}>
          {icon}
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Card({
  children,
  pad = true,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  pad?: boolean;
  className?: string;
  as?: 'div' | 'article' | 'section';
}) {
  return <Tag className={['ui-card', pad && 'ui-card--pad', className].filter(Boolean).join(' ')}>{children}</Tag>;
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  compact,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`ui-empty${compact ? ' ui-empty--compact' : ''}`}>
      {icon && !compact && <div className="ui-empty__art">{icon}</div>}
      {compact ? <p style={{ fontWeight: 600 }}>{title}</p> : <h2>{title}</h2>}
      {children && <div>{children}</div>}
      {action}
    </div>
  );
}

export function RowLink({ to, icon, title, meta, trailing }: { to: string; icon?: ReactNode; title: ReactNode; meta?: ReactNode; trailing?: ReactNode }) {
  return (
    <Link to={to} className="ui-row">
      {icon && <span className="ui-row__icon">{icon}</span>}
      <span className="ui-row__main">
        <span className="ui-row__title">{title}</span>
        {meta && <span className="ui-row__meta">{meta}</span>}
      </span>
      {trailing}
      <ChevronRight className="ui-row__chevron" aria-hidden="true" />
    </Link>
  );
}

export function Badge({ tone, children, icon }: { tone?: 'primary' | 'accent' | 'success' | 'warning' | 'danger'; children: ReactNode; icon?: ReactNode }) {
  return (
    <span className={`ui-badge${tone ? ` ui-badge--${tone}` : ''}`}>
      {icon}
      {children}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-6)' }}>
      <span className="ui-spinner" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

export function ProgressBar({ value, label }: { value: number; label: string }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div className="ui-progress" role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="ui-progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Banner({
  tone,
  icon,
  children,
  action,
  style,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  icon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div className={`ui-banner${tone ? ` ui-banner--${tone}` : ''}`} role={tone === 'danger' ? 'alert' : undefined} style={style}>
      {icon}
      <div style={{ flex: 1 }}>{children}</div>
      {action}
    </div>
  );
}
